import { useAdminCloneJobMutation } from './hooks/useAdminCloneJobMutation';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { buildAsrModelOptions, DEFAULT_ASR_MODEL_VALUE } from '@helpers/asrProxy/buildAsrModelOptions';
import { getAsrModelCapabilities } from '@helpers/asrProxy/getAsrModelCapabilities';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { AdminCloneJobBodyDto, AdminService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import { ModalWrapper } from 'app/modals/core-ui/ModalWrapper';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { createFetchAsrResubmitModelOptionsQueryFn } from 'app/queries/asrProxy/fetchAsrResubmitModelOptions';
import { useEffect, useMemo, useState } from 'react';
import { createFetchCloneTargetOrganizationsQueryFn } from './api/fetchCloneTargetOrganizations';
import { getClonePayloadTranscriptUnlockType } from './helpers/getClonePayloadTranscriptUnlockType';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

const isUrlValid = (url: string) => {
  const urlRe =
    /^(?:demo\.|staging\.|)?autoscript\.ai\/admin\/job-details\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return urlRe.test(url);
};

const isEmailValid = (email: string) => {
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRe.test(email);
};

const getEnvironmentFromUrl = (url: string): 'production' | 'staging' | 'demo' => {
  if (url.startsWith('demo.')) {
    return 'demo';
  }
  if (url.startsWith('staging.')) {
    return 'staging';
  }
  return 'production';
};

export const AdminCloneJobModal = ({ onCancel, onSuccess }: TProps) => {
  const { me } = useAuthContext();

  const [targetUserEmail, setTargetUserEmail] = useState<string>(me.email ?? '');
  const [debouncedTargetUserEmail, setDebouncedTargetUserEmail] = useState(me.email ?? '');
  const [targetOrgId, setTargetOrgId] = useState<string>('');
  const [targetWSId, setTargetWSId] = useState<string>('');
  const [targetJobStatus, setTargetJobStatus] = useState<
    | 'JOB_STATUS_DRAFT'
    | 'JOB_STATUS_SCHEDULED'
    | 'JOB_STATUS_PROCESSING'
    | 'JOB_STATUS_LOCKED'
    | 'JOB_STATUS_READY_FOR_TRANSCRIBING'
    | 'JOB_STATUS_TRANSCRIBING'
    | 'JOB_STATUS_READY_FOR_PROOFREADING'
    | 'JOB_STATUS_PROOFING'
    | 'JOB_STATUS_READY_FOR_FINAL'
    | 'JOB_STATUS_FINALIZING'
    | 'JOB_STATUS_COMPLETE'
    | 'JOB_STATUS_SUCCESS'
    | 'JOB_STATUS_ERROR'
  >('JOB_STATUS_ERROR');
  const [targetJobRecordingSessionType, setTargetJobRecordingSessionType] = useState<
    'RECORDING_SESSION_REMOTE' | 'RECORDING_SESSION_HYBRID' | 'RECORDING_SESSION_IN_PERSON' | 'NONE'
  >('NONE');
  const [targetProcessedAudioFileName, setTargetProcessedAudioFileName] =
    useState<AdminCloneJobBodyDto['processedAudioFileName']>('output.mp3');
  const [targetTranscriptUnlockType, setTargetTranscriptUnlockType] =
    useState<AdminCloneJobBodyDto['transcriptUnlockType']>('SESSION_TEXT');

  const [jobSourceUrl, setJobSourceUrl] = useState<string>('');
  const [sourceEnv, setSourceEnv] = useState<'production' | 'staging' | 'demo' | 'development'>('production');
  const [jobId, setJobId] = useState<string>('');

  const [useSourceJobData, setUseSourceJobData] = useState(true);
  const [jobCustomName, setJobCustomName] = useState<string>('');
  const [reportingType, setReportingType] = useState<'REGULAR' | 'MIXDOWN' | 'CONCATENATE' | 'FTR' | 'UNKNOWN'>(
    'REGULAR',
  );
  const [multipleFilesHandling, setMultipleFilesHandling] = useState<'concat' | 'mixdown'>('mixdown');
  const [resubmit, setResubmit] = useState<boolean>(false);

  const [rushedJobProcessing, setRushedJobProcessing] = useState<boolean>(false);
  const [clearChunksS3Cache, setClearChunksS3Cache] = useState<boolean>(false);
  const [transcriptionModel, setTranscriptionModel] = useState<string>(DEFAULT_ASR_MODEL_VALUE);
  const [useTwoStageClustering, setUseTwoStageClustering] = useState<'default' | 'enabled' | 'disabled'>('default');
  const [enhancedTranscriptMode, setEnhancedTranscriptMode] = useState<
    'auto' | 'force_asone' | 'force_assembly' | undefined
  >('auto');
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);

  const [error, setError] = useState<string>('');

  const { call } = useAPI();

  const cloningAvailabilityQuery = useQuery({
    queryKey: adminJobsKeys.cloneAvailability(),
    queryFn: ({ signal }) => call(() => cancelOnSignal(AdminService.checkIsCloningAvailable(), signal)),
  });
  const isFeatureEnabled = cloningAvailabilityQuery.data?.isEnabled ?? false;
  useEffect(() => {
    const environment = cloningAvailabilityQuery.data?.currentEnvironment;
    if (
      environment === 'production' ||
      environment === 'staging' ||
      environment === 'demo' ||
      environment === 'development'
    ) {
      setSourceEnv(environment);
    }
  }, [cloningAvailabilityQuery.data?.currentEnvironment]);

  const modelOptionsQuery = useQuery({
    queryKey: asrProxyKeys.resubmitModelOptions(),
    queryFn: createFetchAsrResubmitModelOptionsQueryFn(call),
  });
  const asrProxyDisabled = modelOptionsQuery.data?.asrProxyDisabled ?? false;
  const proxyModels = modelOptionsQuery.data?.models ?? [];
  const defaultModel = modelOptionsQuery.data?.defaultModel ?? '';
  const selectedModel = proxyModels.find(
    (model) => model.name === (transcriptionModel === DEFAULT_ASR_MODEL_VALUE ? defaultModel : transcriptionModel),
  );
  const { supportsTwoStageClustering } = getAsrModelCapabilities(asrProxyDisabled ? undefined : selectedModel);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedTargetUserEmail(targetUserEmail.trim());
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [targetUserEmail]);

  useEffect(() => {
    setTargetOrgId('');
    setTargetWSId('');
  }, [targetUserEmail]);

  const isTargetEmailSettling = targetUserEmail.trim() !== debouncedTargetUserEmail;
  const organizationsQuery = useQuery({
    queryKey: adminJobsKeys.cloneTargets(debouncedTargetUserEmail),
    queryFn: createFetchCloneTargetOrganizationsQueryFn(call),
    enabled: !isTargetEmailSettling && isEmailValid(debouncedTargetUserEmail),
  });
  const orgList = useMemo(
    () =>
      new Map(
        isTargetEmailSettling || !isEmailValid(debouncedTargetUserEmail)
          ? []
          : (organizationsQuery.data ?? []).map((organization) => [organization.id, organization]),
      ),
    [organizationsQuery.data, isTargetEmailSettling, debouncedTargetUserEmail],
  );

  const cloneMutation = useAdminCloneJobMutation(onSuccess);
  const isLoading = cloneMutation.isPending;

  return (
    <>
      {isLoading ? <AbsoluteSpinner overlay={true} className="!fixed !inset-0 !z-[10001]" /> : null}
      <ModalWrapper width="570px" overflow="auto">
        <h2>Admin Clone Job</h2>

        <div className="p-4 rounded-md mt-4 mb-4">
          <h4 className="flex w-full justify-start items-start underline">User details</h4>
          <div className="mt-3">
            <small className="w-full flex items-start">Insert target user email</small>
            <div className="mb-2">
              <Input
                label="Target User Email"
                value={targetUserEmail}
                onChange={(e) => setTargetUserEmail(e.target.value)}
                error={!!targetUserEmail && !isEmailValid(targetUserEmail)}
              />
            </div>

            <div className="flex justify-between">
              <div className="mb-2 w-full">
                <small className="w-full flex items-start">Select target user organization</small>
                <Select
                  options={Array.from(orgList.values()).map((org) => ({
                    label: org.name,
                    value: org.id,
                  }))}
                  label="Organization"
                  onChange={(_, value) => setTargetOrgId(value)}
                  value={targetOrgId}
                  disabled={!targetUserEmail || orgList.size === 0}
                />
              </div>

              <div className="mb-4 w-full ml-4">
                <small className="w-full flex items-start">Select target user workspace</small>
                <Select
                  options={
                    orgList.get(targetOrgId)?.workspaces.map((ws) => ({
                      label: ws.name ?? 'Unnamed workspace',
                      value: ws.id,
                    })) || []
                  }
                  label="Workspace"
                  onChange={(_, value) => setTargetWSId(value)}
                  value={targetWSId}
                  disabled={!targetOrgId || !targetUserEmail || orgList.size === 0}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-md mt-4 mb-4">
          <h4 className="mt-5 flex w-full justify-start items-start underline">Target job details</h4>

          <div className="w-full mt-4 flex items-start justify-start grid-gap-2">
            <CheckBox
              name="Use source job data"
              onChange={() => {
                setUseSourceJobData((prev) => !prev);
              }}
              checked={useSourceJobData}
            ></CheckBox>
            <small>Use source job data</small>
          </div>
          {!useSourceJobData ? (
            <>
              <div className="mb-2 mt-3 ">
                <small className="w-full flex items-start">Target job name</small>
                <Input
                  label="Job custom name"
                  value={jobCustomName}
                  onChange={(e) => setJobCustomName(e.target.value)}
                />
              </div>
              <div className="flex justify-between">
                <div className="mb-4 w-full mr-4">
                  <small className="w-full flex items-start">Select target job status</small>
                  <Select
                    options={[
                      'JOB_STATUS_DRAFT',
                      'JOB_STATUS_SCHEDULED',
                      'JOB_STATUS_PROCESSING',
                      'JOB_STATUS_LOCKED',
                      'JOB_STATUS_READY_FOR_TRANSCRIBING',
                      'JOB_STATUS_TRANSCRIBING',
                      'JOB_STATUS_READY_FOR_PROOFREADING',
                      'JOB_STATUS_PROOFING',
                      'JOB_STATUS_READY_FOR_FINAL',
                      'JOB_STATUS_FINALIZING',
                      'JOB_STATUS_COMPLETE',
                      'JOB_STATUS_SUCCESS',
                      'JOB_STATUS_ERROR',
                    ].map((status) => ({ label: status, value: status }))}
                    label="Job Status"
                    onChange={(_, value) => setTargetJobStatus(value as typeof targetJobStatus)}
                    value={targetJobStatus}
                  />
                </div>
                <div className="mb-4 w-full">
                  <small className="w-full flex items-start">Select target job recording session type</small>
                  <Select
                    options={[
                      'RECORDING_SESSION_REMOTE',
                      'RECORDING_SESSION_HYBRID',
                      'RECORDING_SESSION_IN_PERSON',
                      'NONE',
                    ].map((type) => ({ label: type, value: type }))}
                    label="Recording Session Type"
                    onChange={(_, value) =>
                      setTargetJobRecordingSessionType(value as typeof targetJobRecordingSessionType)
                    }
                    value={targetJobRecordingSessionType}
                  />
                </div>
              </div>
              <div className="flex justify-between">
                <div className="mb-4 w-full">
                  <small className="w-full flex items-start">Select target job processed audio file name</small>
                  <Select
                    options={['output.mp3', 'output.wav', 'output.flac'].map((type) => ({ label: type, value: type }))}
                    label="Processed audio file name"
                    onChange={(_, value) =>
                      setTargetProcessedAudioFileName(value as AdminCloneJobBodyDto['processedAudioFileName'])
                    }
                    value={targetProcessedAudioFileName}
                  />
                </div>
                <div className="mb-4 ml-4 w-full justify-start">
                  <small className="w-full flex items-start">Select target job transcript unlock type</small>
                  <Select
                    options={['SESSION_TEXT', 'ASONE_ENHANCED'].map((type) => ({
                      label: type,
                      value: type,
                    }))}
                    label="Transcript Unlock Type"
                    onChange={(_, value) =>
                      setTargetTranscriptUnlockType(value as AdminCloneJobBodyDto['transcriptUnlockType'])
                    }
                    value={targetTranscriptUnlockType}
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <div className="mb-4 w-full">
                  <small className="w-full flex items-start">Select target job reporting type</small>
                  <Select
                    options={['REGULAR', 'MIXDOWN', 'CONCATENATE', 'FTR', 'UNKNOWN'].map((type) => ({
                      label: type,
                      value: type,
                    }))}
                    label="Reporting Type"
                    onChange={(_, value) => setReportingType(value as typeof reportingType)}
                    value={reportingType}
                  />
                </div>
                <div className="mb-4 ml-4 w-full justify-start">
                  <small className="w-full flex items-start">Select target job multiple files handling</small>
                  <Select
                    options={['concat', 'mixdown'].map((type) => ({
                      label: type,
                      value: type,
                    }))}
                    label="Multiple Files Handling"
                    onChange={(_, value) => setMultipleFilesHandling(value as typeof multipleFilesHandling)}
                    value={multipleFilesHandling}
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="p-4 rounded-md mb-4">
          <h4 className="mt-5 flex w-full justify-start items-start underline">Source job details</h4>
          <div className="flex w-full justify-between">
            <div className="mt-4 w-full">
              <small className="w-full flex justify-start items-start">{'Insert Admin Details Job Source Url'}</small>
              <Input
                disabled={!isFeatureEnabled || (!!jobId && !jobSourceUrl)}
                label="Admin Details Job Source"
                value={jobSourceUrl}
                onChange={(e) => {
                  setJobId('');

                  const url = e.target.value.replace('https://', '').replace('www.', '');
                  if (!isUrlValid(url) && url.length > 0) {
                    setError(
                      'Invalid Admin Details Job URL. Please insert valid url in format https://<env>.autoscript.ai/admin/job-details/<job-id>',
                    );
                    return;
                  }

                  setError('');
                  setJobSourceUrl(url);
                  setJobId(url.split('/admin/job-details/')[1]);
                  setSourceEnv(getEnvironmentFromUrl(url));
                }}
              />
            </div>

            <div className="w-full mt-4 ml-4">
              <small className="w-full flex justify-start items-start">Source Job Id</small>
              <Input
                label="Source Job ID"
                value={jobId}
                disabled={!!jobSourceUrl}
                onChange={(e) => {
                  setJobId(e.target.value);
                }}
              />
            </div>
          </div>

          <div className="w-[50%] mt-2 mr-4">
            <small className="w-full flex justify-start items-start">Source Environment</small>
            <Select
              options={['production', 'staging', 'demo', 'development'].map((env) => ({ label: env, value: env }))}
              label="Environment"
              onChange={(_, value) => setSourceEnv(value as typeof sourceEnv)}
              value={sourceEnv}
              disabled={!!jobSourceUrl || !isFeatureEnabled}
            />
          </div>
          <div className="w-full mt-4 mb-4 flex items-start gap-2">
            <CheckBox
              name="Resubmit job after cloning"
              onChange={() => setResubmit((prev) => !prev)}
              checked={resubmit}
            />
            <small>Resubmit job after cloning</small>
          </div>

          {!!resubmit && (
            <>
              <div className="flex justify-between mt-4">
                <div className="w-full mr-4">
                  <small className="w-full flex items-start">Clear chunks and metadata S3 cache</small>
                  <Select
                    options={[
                      { label: 'Yes', value: '1' },
                      { label: 'No', value: '2' },
                    ]}
                    label="Clear chunks and metadata S3 cache"
                    onChange={(_, value) => setClearChunksS3Cache(value === '1')}
                    value={clearChunksS3Cache ? '1' : '2'}
                  />
                </div>

                <div className="w-full">
                  <small className="w-full flex items-start">Rushed job processing</small>
                  <Select
                    options={[
                      { label: 'Yes', value: '1' },
                      { label: 'No', value: '2' },
                    ]}
                    label="Rushed job processing"
                    onChange={(_, value) => setRushedJobProcessing(value === '1')}
                    value={rushedJobProcessing ? '1' : '2'}
                  />
                </div>
              </div>

              <DisclosureSection
                title="Advanced options"
                open={advancedOpen}
                setOpenState={setAdvancedOpen}
                loading={false}
                className="mt-4"
                titleClassName="!bg-[var(--color-surface)]"
              >
                <div className="flex justify-between mt-6">
                  {!asrProxyDisabled ? (
                    <div className="w-full mr-4">
                      <small className="w-full flex items-start">ASR proxy model</small>
                      <Select
                        options={buildAsrModelOptions({
                          models: proxyModels,
                          defaultModel,
                          selectedModel: transcriptionModel,
                        })}
                        label="ASR proxy model"
                        onChange={(_, value) => setTranscriptionModel(value)}
                        value={transcriptionModel}
                        disabled={modelOptionsQuery.isFetching}
                      />
                      {selectedModel && !selectedModel.healthy ? (
                        <small className="w-full flex items-start text-[var(--color-error-heading)]">
                          The last health check failed
                          {selectedModel.healthDetail ? `: ${selectedModel.healthDetail}` : ''}
                        </small>
                      ) : null}
                    </div>
                  ) : null}

                  {supportsTwoStageClustering ? (
                    <div className="w-full">
                      <small className="w-full flex items-start">Two-stage clustering</small>
                      <Select
                        options={[
                          { label: 'Default', value: 'default' },
                          { label: 'Yes', value: 'enabled' },
                          { label: 'No', value: 'disabled' },
                        ]}
                        label="Two-stage clustering"
                        onChange={(_, value) => setUseTwoStageClustering(value as 'default' | 'enabled' | 'disabled')}
                        value={useTwoStageClustering}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="flex justify-between mt-4">
                  <div className="w-full mr-4">
                    <small className="w-full flex items-start">Enhanced transcript selection</small>
                    <Select
                      options={[
                        { label: 'Auto', value: 'auto' },
                        {
                          label: 'Force ASOne transcript.json',
                          value: 'force_asone',
                        },
                        { label: 'Force asr.json', value: 'force_assembly' },
                      ]}
                      label="Enhanced transcript selection"
                      onChange={(_, value) =>
                        setEnhancedTranscriptMode(value as 'auto' | 'force_asone' | 'force_assembly')
                      }
                      value={enhancedTranscriptMode}
                    />
                  </div>
                </div>
              </DisclosureSection>
            </>
          )}
        </div>

        <div className="text-sm mb-5 text-red-500">
          {error && <small>{error}</small>}
          {!isFeatureEnabled && (
            <small className="flex w-full justify-center items-center mt-2 mb-4">
              Cross-environment cloning is currently disabled.
            </small>
          )}
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (cloneMutation.isPending) {
              return;
            }
            cloneMutation.mutate({
              targetUserEmail: targetUserEmail.trim(),
              targetWorkspaceId: targetWSId,
              sourceEnvironment: sourceEnv,
              jobId,
              jobName: jobCustomName,
              jobStatus: targetJobStatus,
              recordingSessionType:
                targetJobRecordingSessionType === 'NONE' ? undefined : targetJobRecordingSessionType,
              processedAudioFileName: targetProcessedAudioFileName,
              transcriptUnlockType: getClonePayloadTranscriptUnlockType(useSourceJobData, targetTranscriptUnlockType),
              resubmitAfterCloning: resubmit,
              useSourceJobData,
              resubmitOptions: resubmit
                ? {
                    rushedJob: rushedJobProcessing,
                    clearStylefixCache: clearChunksS3Cache,
                    chosenTranscriptionModel:
                      !asrProxyDisabled && transcriptionModel !== DEFAULT_ASR_MODEL_VALUE
                        ? transcriptionModel
                        : undefined,
                    useAssemblyTwoStageClustering: supportsTwoStageClustering
                      ? useTwoStageClustering === 'enabled'
                        ? true
                        : useTwoStageClustering === 'disabled'
                          ? false
                          : undefined
                      : undefined,
                    llmTranscriptSelectionMode: enhancedTranscriptMode,
                  }
                : undefined,
            });
          }}
        >
          <div className="buttonContainer flex justify-between">
            <OutlinedButton
              type="button"
              onClick={() => {
                cloneMutation.cancel();
                onCancel();
              }}
            >
              Cancel
            </OutlinedButton>
            <Button
              disabled={cloneMutation.isPending || !targetUserEmail || !jobId || !targetWSId || !sourceEnv}
              type="submit"
            >
              Clone
            </Button>
          </div>
        </form>
      </ModalWrapper>
    </>
  );
};
